// StatusBadge - Status indicator for orders
import clsx from 'clsx'

import { getStatusLabel } from '../../constants/orderStatus'

const statusStyles: Record<string, string> = {
  // Needs Attention (Amber) - awaiting action
  awaiting_approval: 'bg-amber-100 text-amber-700 border border-amber-200',

  // Submitting (Sky) - retry loop while the order is being pushed to the
  // supplier; distinct from "Sent to supplier" which is post-ack.
  started_processing: 'bg-sky-100 text-sky-700 border border-sky-200',

  // Approved / Sent to supplier (Blue) - approved by tenant OR handed off
  // and waiting on supplier ack.
  approved: 'bg-blue-100 text-blue-700 border border-blue-200',
  awaiting_supplier: 'bg-blue-100 text-blue-700 border border-blue-200',

  // Processing (Indigo) - actively being worked on
  processing: 'bg-indigo-100 text-indigo-700 border border-indigo-200',

  // In Transit (Purple) - on the way
  out_for_delivery: 'bg-purple-100 text-purple-700 border border-purple-200',

  // Success (Green) - ready or completed
  ready_to_collect: 'bg-green-100 text-green-700 border border-green-200',
  completed: 'bg-green-100 text-green-700 border border-green-200',

  // Failed/Declined (Red) - errors and rejections
  submission_failed: 'bg-red-100 text-red-700 border border-red-200',
  internal_decline: 'bg-red-100 text-red-700 border border-red-200',
  fulfillment_declined: 'bg-red-100 text-red-700 border border-red-200',
  supplier_cancelled: 'bg-red-100 text-red-700 border border-red-200',

  // Cancelled (Gray) - neutral cancellation
  cancelled: 'bg-gray-100 text-gray-700 border border-gray-200',
  withdrawn: 'bg-gray-100 text-gray-500 border border-gray-200',

  // Legacy statuses
  confirmed: 'bg-green-100 text-green-700 border border-green-200',
  pending_approval: 'bg-amber-100 text-amber-700 border border-amber-200',
  pending: 'bg-amber-100 text-amber-700 border border-amber-200',
}

// Legacy statuses not covered by the shared label map — kept locally so the
// badge still renders them if they ever appear on old rows.
const LEGACY_STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  pending_approval: 'Pending approval',
  pending: 'Pending',
}

const statusDots: Record<string, boolean> = {
  awaiting_approval: true,
  pending_approval: true,
  pending: true,
  awaiting_supplier: true,
  processing: true,
  out_for_delivery: true,
}

export interface StatusBadgeProps {
  status: string
  className?: string
  showDot?: boolean
}

export function StatusBadge({ status, className, showDot = true }: StatusBadgeProps) {
  const label = LEGACY_STATUS_LABELS[status || ''] || getStatusLabel(status)
  const shouldShowDot = showDot && statusDots[status || '']
  const style = statusStyles[status] || 'bg-gray-100 text-gray-700 border border-gray-200'

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
        style,
        className
      )}
    >
      {shouldShowDot && (
        <span className="inline-flex rounded-full h-2 w-2 bg-current"></span>
      )}
      {label}
    </span>
  )
}
